import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Heart, Mail, Phone } from "lucide-react";
import { Logo } from "./Logo";
import { Reveal } from "./Reveal";
import { CONTACT } from "../../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LikeButton = ({ t }) => {
  const [count, setCount] = useState(null);
  const [liked, setLiked] = useState(false);

  useEffect(() => {
    setLiked(localStorage.getItem("site_liked") === "1");
    fetch(`${API}/likes`)
      .then((r) => r.json())
      .then((d) => setCount(d.count))
      .catch(() => setCount(0));
  }, []);

  const like = async () => {
    if (liked) return;
    setLiked(true);
    localStorage.setItem("site_liked", "1");
    setCount((c) => (c ?? 0) + 1);
    try {
      const r = await fetch(`${API}/likes`, { method: "POST" });
      const d = await r.json();
      setCount(d.count);
    } catch (e) {
      /* optimistic count stays */
    }
  };

  return (
    <div className="mt-12 flex flex-col items-center gap-3" data-testid="like-section">
      <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#8FA396]">
        {liked ? t.footer.likeThanks : t.footer.likePrompt}
      </p>
      <div className="flex items-center gap-3">
        <motion.button
          data-testid="like-button"
          onClick={like}
          whileTap={{ scale: 0.85 }}
          whileHover={{ scale: liked ? 1 : 1.08 }}
          aria-pressed={liked}
          className={`flex h-14 w-14 items-center justify-center rounded-full border transition-all duration-300 ${
            liked
              ? "border-[#C49A5A] bg-[#C49A5A] text-[#1E2721] shadow-lg shadow-[#C49A5A]/30"
              : "border-[#FAF9F5]/25 text-[#FAF9F5] hover:border-[#C49A5A] hover:text-[#C49A5A]"
          }`}
        >
          <motion.span
            key={liked ? "liked" : "idle"}
            initial={{ scale: 0.4 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 400, damping: 12 }}
          >
            <Heart size={22} fill={liked ? "currentColor" : "none"} />
          </motion.span>
        </motion.button>
        <span data-testid="like-count" className="font-display text-2xl italic text-[#FAF9F5]">
          {count ?? "—"}
          <span className="ml-2 font-body text-xs not-italic uppercase tracking-[0.15em] text-[#8FA396]">
            {t.footer.likeCount}
          </span>
        </span>
      </div>
    </div>
  );
};

export const Footer = ({ t }) => (
  <footer id="contatti" className="bg-[#1E2721] py-20 text-[#E6ECE8] sm:py-28" data-testid="footer">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal className="mx-auto max-w-3xl text-center">
        <div className="flex justify-center">
          <Logo size={56} />
        </div>
        <h2 className="mt-6 font-display text-4xl font-light tracking-tight text-[#FAF9F5] sm:text-5xl lg:text-6xl">
          {t.footer.title}
        </h2>
        <p className="mt-4 text-base leading-relaxed text-[#C9D8CE]">{t.footer.sub}</p>

        <div className="mt-10 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
          <a
            href={`tel:+39${CONTACT.phoneTorino}`}
            data-testid="footer-phone-torino"
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#C49A5A] px-6 py-3.5 text-sm font-semibold text-[#1E2721] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#D4AB6C] hover:shadow-xl sm:w-auto"
          >
            <Phone size={15} />
            {t.footer.torinoLabel} · {CONTACT.phoneTorinoLabel}
          </a>
          <a
            href={`tel:+39${CONTACT.phoneCarignano}`}
            data-testid="footer-phone-carignano"
            className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-[#FAF9F5]/25 px-6 py-3.5 text-sm font-semibold text-[#FAF9F5] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#FAF9F5]/10 sm:w-auto"
          >
            <Phone size={15} />
            {t.footer.carignanoLabel} · {CONTACT.phoneCarignanoLabel}
          </a>
          <a
            href={`mailto:${CONTACT.email}`}
            data-testid="footer-email-link"
            className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-[#FAF9F5]/25 px-6 py-3.5 text-sm font-semibold text-[#FAF9F5] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#FAF9F5]/10 sm:w-auto"
          >
            <Mail size={15} />
            {CONTACT.email}
          </a>
        </div>
      </Reveal>

      <Reveal delay={0.1}>
        <LikeButton t={t} />
      </Reveal>

      <div className="mt-16 flex flex-col items-center justify-between gap-3 border-t border-[#FAF9F5]/10 pt-8 text-xs text-[#8FA396] sm:flex-row">
        <span>Dott.ssa Claudia D'Elia — {t.footer.role}</span>
        <span>
          © {new Date().getFullYear()} · {t.footer.rights}
        </span>
      </div>
    </div>
  </footer>
);
